import { Button, Center, Checkbox, Group, Input, Loader, Pagination, Table, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import { openConfirmModal, openModal } from '@mantine/modals';
import { IconDownload } from '@tabler/icons-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DictionaryItem,
  generateCSV,
  getDictionaryService,
  useBot,
  useDateLocal,
  useDictionaryList,
  useDictionaryMethods,
} from '@mygpt/app';
import { SvgAdd, SvgSearch, SvgTrash } from '@/components/Common/icon';
import { DictionaryModal } from '@/components/Dictionary/Modal';
import ContainerHeader from '@/components/Headers/ContainerHeader';

import classes from './index.module.css';
import { Item } from './Item';

dayjs.extend(utc);

const DictionaryTable = () => {
  const { t } = useTranslation();
  const { botId } = useBot();
  const { dateLocale } = useDateLocal();
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);

  const { deleteDictionary } = useDictionaryMethods();
  const {
    data: tableData,
    dateValue,
    getQueryParams,
    loadList,
    loading: tableLoading,
    page,
    search,
    setDateValue,
    setPage,
    setSearch,
  } = useDictionaryList();

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 500);

  useEffect(() => {
    setSearch(debouncedSearch);
  }, [debouncedSearch]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const allSelected = useMemo(
    () => selectedIds.length > 0 && tableData?.length === selectedIds.length,
    [tableData, selectedIds]
  );

  const toggleAllChecked = useCallback(
    (checked: boolean) => {
      setSelectedIds(checked ? tableData.map((item) => item.id) : []);
    },
    [tableData]
  );

  const handleSelect = useCallback((dictionaryId: string) => {
    setSelectedIds((ids) => {
      if (ids.includes(dictionaryId)) {
        return ids.filter((id) => id !== dictionaryId);
      }
      return [...ids, dictionaryId];
    });
  }, []);

  const handleSave = () => {
    loadList(getQueryParams({ page: 1 }));
    setSelectedIds([]);
  };

  const handleCreateDictionary = () => {
    openModal({
      withinPortal: false,
      centered: true,
      radius: 'md',
      size: 'lg',
      children: <DictionaryModal type="create" onSave={handleSave} />,
    });
  };

  const handleEdit = (item: DictionaryItem) => {
    openModal({
      withinPortal: false,
      centered: true,
      radius: 'md',
      size: 'lg',
      children: <DictionaryModal type="edit" onSave={handleSave} item={item} />,
    });
  };

  const handleDelete = async (item?: DictionaryItem) => {
    if (item) {
      await deleteDictionary({
        aiId: botId,
        dictionaryIds: [item.id],
      });
    } else if (selectedIds.length) {
      await deleteDictionary({
        aiId: botId,
        dictionaryIds: selectedIds,
      });
    }
    handleSave();
  };

  const deleteConfirm = () => {
    if (!selectedIds.length) return;
    openConfirmModal({
      title: t('faqs.delete.title'),
      centered: true,
      children: <span dangerouslySetInnerHTML={{ __html: t('faqs.delete.confirm') }} />,
      labels: { confirm: t('delete_btn'), cancel: t('alert_cancel') },
      onCancel: () => console.log('Cancel'),
      confirmProps: {
        color: 'red',
        onClick: () => handleDelete(),
      },
    });
  };

  const handleDownload = () => {
    setDownloadLoading(true);

    getDictionaryService(
      Object.assign(
        {
          aiId: botId,
          search,
          page: 1,
          size: 1000,
          order_by: '-updated_at',
        },
        dateValue[0] &&
          dateValue[1] && {
            start_date: dayjs(dateValue[0]).utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
            end_date: dayjs(dateValue[1]).endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
          }
      )
    )
      .then((res) => {
        const data = res.data;
        const { items } = data;
        if (!items || !Array.isArray(items)) return;

        const time =
          dateValue[0] && dateValue[1]
            ? `_${dayjs(dateValue[0]).format('YYYY.MM.DD')}_${dayjs(dateValue[1]).format('YYYY.MM.DD')}`
            : '';

        const fileName = `${t('menu.bot.dictionary')}${time}`;
        // 创建CSV的头部行
        const header = [
          t('dictionary.table.source'),
          t('dictionary.table.target'),
          t('embeddings.table.last_modified'),
        ];

        // 将每个数据对象转换为CSV格式的一行
        if (!data) return;
        const rows = items.map((item) => {
          return [item.source, item.target, dayjs(item.updated_at).format('YYYY-MM-DD HH:mm:ss')];
        });
        const csvData = {
          header,
          rows,
        };

        generateCSV(fileName, csvData);
      })
      .finally(() => {
        setDownloadLoading(false);
      });
  };

  useEffect(() => {
    if ((dateValue[0] && dateValue[1]) || (!dateValue[0] && !dateValue[1])) {
      setSelectedIds([]);
    }
  }, [dateValue, search, page.page]);

  return (
    <div>
      <ContainerHeader title={t('page.dictionary_title')} description={t('page.dictionary_description')} />
      <Group justify="space-between" mb={20}>
        <Group>
          <Button leftSection={<SvgAdd />} onClick={handleCreateDictionary}>
            {t('dictionary.create')}
          </Button>
          <Button
            color="red"
            variant="outline"
            className="filled"
            leftSection={<SvgTrash />}
            disabled={!selectedIds.length}
            onClick={() => deleteConfirm()}
          >
            {t('dictionary.delete')}
          </Button>
        </Group>

        <Group>
          <Input
            className="grow xl:grow-0 xl:min-w-[30%]"
            placeholder={t('dictionary.search_placeholder')}
            leftSection={<SvgSearch />}
            value={searchValue}
            onChange={(e) => setSearchValue(e.currentTarget.value)}
          />

          <DatePickerInput
            type="range"
            locale={dateLocale}
            size="sm"
            value={dateValue}
            onChange={setDateValue}
            valueFormat="YYYY-MM-DD"
            numberOfColumns={2}
            allowSingleDateInRange
          />

          <Button disabled={!tableLoading && !tableData.length} onClick={handleDownload} loading={downloadLoading}>
            <IconDownload size={16} />
          </Button>
        </Group>
      </Group>

      <Table highlightOnHover className={classes.table} captionSide="bottom">
        <Table.Thead className="bg-gray-300">
          <Table.Tr>
            <Table.Th className="w-1/4">
              <Group gap="xs" wrap="nowrap">
                <Checkbox checked={allSelected} onChange={() => toggleAllChecked(!allSelected)} />
                {t('dictionary.table.source')}
              </Group>
            </Table.Th>
            <Table.Th className="w-1/2">{t('dictionary.table.target')}</Table.Th>
            <Table.Th className="w-[180px]">{t('embeddings.table.last_modified')}</Table.Th>
            <Table.Th className="w-[100px]">
              <Text ta="center">{t('embeddings.table.action')}</Text>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {!tableLoading &&
            tableData.map((item) => (
              <Item
                key={item.id}
                item={item}
                selected={selectedIds.includes(item.id)}
                onSelectedChange={() => handleSelect(item.id)}
                onEdit={(item) => handleEdit(item)}
                onDelete={(item) => handleDelete(item)}
              />
            ))}
        </Table.Tbody>
      </Table>

      {tableLoading && (
        <Center className="h-[300px] bg-white flex items-center">
          <Loader />
        </Center>
      )}

      {!tableLoading && !tableData.length && (
        <Center className="h-[300px] bg-white flex items-center">
          <Text color="gray.5">{t('no_data')}</Text>
        </Center>
      )}

      {!!page.total && (
        <Group justify="space-between" mt={15}>
          <Text size={'sm'} c="gray.5">
            {t('teach_model.table.total_items', { number: page.total })}
          </Text>
          <Pagination
            value={page.page}
            size={'sm'}
            onChange={(value) => {
              setPage({ page: value });
            }}
            total={page.pages}
            color="violet.5"
          />
        </Group>
      )}
    </div>
  );
};

export default DictionaryTable;
